<script context="module" lang="ts">
  import { someTruthyProperty } from "app/utils/ts-helpers";

  export type CheckBox = { id: string; label: string; value: boolean };
  export function validateCheckbox(value: Record<string, boolean>) {
    return someTruthyProperty(value) || "Please select at least one option";
  }
</script>

<script lang="ts">
  import WrapField from "$components/form/Fields/WrapField.svelte";
  import { getContext, onMount, createEventDispatcher } from "svelte";
  import type { createFormStore } from "$components/form/store";

  // The component accepts 'options', an array of 'CheckBox' objects.
  // Each 'CheckBox' has 'id', 'label', and 'value' properties:
  //   - 'id': unique identifier for the checkbox.
  //   - 'label': text displayed next to the checkbox.
  //   - 'value': initial checked state (boolean), useful even without a form.
  // Adds each option as a separate reference in the form store using the option's 'id' as the name.
  //
  // When the selection changes, the component:
  //   - Dispatches an event with an array of IDs of all selected options.
  //   - Sets the form value with all selected checkboxes if used within a form.
  //   - Updates the parent's 'value' if bound.
  //
  // This allows for both form-based and standalone usage, keeping the parent component updated with the current selection.
  export let options: CheckBox[];
  export let value: string[] = options.filter((option) => option.value).map((option) => option.id); // this is an array of ids of the selected options
  export let validationRules: { [key: string]: any } = {};
  export let inputClassname: string = "";
  export let labelClassname: string = "";
  export let wrapperClassname: string = "";
  export let help: string = "";
  export let label: string = "";

  const numColumns = options.length > 2 ? 2 : 1;
  const formStore: ReturnType<typeof createFormStore> | undefined = getContext("form");
  const dispatch = createEventDispatcher();

  let refs: HTMLInputElement[] = [];

  function onSelect() {
    // Update 'value' with IDs of selected options, it is at this scope to allow for binding on the parent even if inside a form
    value = options.filter((option) => option.value).map((option) => option.id);

    // Update form store with the new options values
    if (formStore) {
      options.forEach((option) => {
        formStore.setField(option.id, option.value);
      });
    } else {
      // dispatch array of IDs of selected options
      dispatch("change", value);
    }
  }

  onMount(() => {
    if (formStore) {
      options.forEach((option, i) => {
        formStore.addRef(option.id, refs[i]);
      });
    }
  });
</script>

<WrapField
  {wrapperClassname}
  {labelClassname}
  {label}
  {validationRules}
  {help}
  name={options.map((option) => option.id).join(",")}
>
  <div class="grid gap-4" style="grid-template-columns: repeat({numColumns}, 1fr)">
    {#each options as option, i}
      <label class="flex w-fit cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          class="w-4 cursor-pointer rounded border-gray-400
          text-gray-800 checked:border-transparent checked:bg-brand-blurple hover:border-brand-blurple
          checked:hover:bg-brand-blurple/80 focus:ring-transparent focus:ring-offset-transparent focus:checked:bg-brand-blurple
          dark:border-gray-400 dark:bg-transparent dark:checked:border-transparent dark:checked:bg-brand-blurple
          dark:hover:border-brand-blurple dark:checked:hover:bg-brand-blurple/80 dark:focus:border-gray-300
          {inputClassname}"
          bind:this={refs[i]}
          bind:checked={option.value}
          on:change={onSelect}
        />
        <span class="text-sm font-medium text-gray-400 dark:text-gray-400">{option.label}</span>
      </label>
    {/each}
  </div>
</WrapField>
